import { fetchOrders } from '@/lib/api/income/api';
import { OrderParamsRequest } from '@/lib/api/income/types';
import { QUERY_KEYS } from '@/lib/constants';
import { useStanQuery } from '@/queries';
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useOrders(params: Omit<OrderParamsRequest, 'page'>) {
    const [queryParams, setQueryParams] = useSearchParams();
    const page = queryParams.get('page') ?? '1';
    const [currentPage, setCurrentPage] = useState(Number(page));

    // Sync currentPage state with URL parameter
    useEffect(() => {
        const pageFromUrl = Number(queryParams.get('page') ?? '1');
        setCurrentPage(pageFromUrl);
    }, [queryParams]);

    const query = useStanQuery({
        queryKey: [QUERY_KEYS.ORDERS, params, currentPage],
        queryFn: ({ signal }) => fetchOrders({ ...params, page: currentPage }, signal),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000 * 5,
        retry: 2,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        meta: { error: { sentryMessage: '[ORDERS] failed-to-fetch-orders' } },
    });

    const goToNextPage = () => {
        if (query.data && currentPage < query.data.last_page) {
            setCurrentPage(prev => prev + 1);
            setQueryParams(prev => {
                const page = parseInt(prev.get('page') ?? '1') + 1;
                prev.set('page', page.toString());
                return prev;
            });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
            setQueryParams(prev => {
                const page = parseInt(prev.get('page') ?? '1') - 1;
                prev.set('page', page.toString());
                return prev;
            });
        }
    };

    const goToPage = (page: number) => {
        if (page >= 1 && query.data && page <= query.data.last_page) {
            setCurrentPage(page);
            setQueryParams(prev => {
                prev.set('page', page.toString());
                return prev;
            });
        }
    };

    return {
        ...query,
        currentPage,
        totalPages: query.data?.last_page ?? 0,
        totalItems: query.data?.total ?? 0,
        items: query.data?.data ?? [],
        hasNextPage: query.data ? query.data.current_page < query.data.last_page : false,
        hasPreviousPage: currentPage > 1,
        goToNextPage,
        goToPreviousPage,
        goToPage,
    };
}
